import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import type {User, Session} from '@supabase/supabase-js';
import type {VideoEditor} from '../../backend-types/user';
import {
  initializeAuth,
  signIn,
  signOut,
  setAccountPassword,
  confirmEmailLink,
  requestPasswordReset,
  updatePassword,
  changePassword,
  changeEmail,
  updateUserProfile,
  updateVideoEditorPreference,
  fetchUserProfile,
  setWelcomePagePreference,
} from './authThunks';
import {ApiState, createInitialApiState, setApiPending, setApiSuccess, setApiRejected} from '../apiState';
import {apiErrorMessage} from '../../lib/apiError';

export interface AppUser {
    userId: string;
    email: string;
    name: string | null;
    avatar_url: string | null;
    is_admin: boolean;
    subscribed: boolean;
    subscription_plan: string;
    subscription_expires_at: string | null;
    welcome_page: boolean;
    /**
     * False for an account provisioned from a Paddle checkout that has never chosen a password —
     * the set-password screen goes in front of it. Optional because a profile cached before the
     * field existed simply does not have it, and "missing" has to mean unknown rather than false.
     */
    password_set?: boolean;
    /**
     * Which editor this user cuts in. Optional for the same reason as `password_set` — a profile
     * cached before this field shipped simply lacks it — but unlike that one, "missing" and "null"
     * mean the exact same thing here (we've never asked), so there is nothing to special-case: read
     * this through `prefersFullMediaPathSelector` (`store/selectors/auth.selectors.ts`), never by
     * comparing it to a literal.
     */
    video_editor?: VideoEditor | null;
    /**
     * Server-stamped, and nothing reads it any more — the DaVinci Resolve warning dialog it
     * recorded an acknowledgement for is gone, since Resolve does not hang on a media reference it
     * cannot find. Kept only because the profile response still carries it. Never write it.
     */
    resolve_warning_acknowledged_at?: string | null;
    created_at: string;
    updated_at: string;
}

interface AuthState {
    user: User | null;
    session: Session | null;
    appUser: AppUser | null;
    loading: boolean;
    initialized: boolean;
    error: string | null;
    /**
     * Set the moment `POST /rest/v1/user/password` succeeds. It outranks both the profile row and the
     * JWT claim, because the claim is stamped at token issue and keeps saying false until the session
     * refreshes — without this the user would be sent straight back to the set-password screen.
     */
    passwordSetConfirmed: boolean;
    api: {
        initialize: ApiState;
        signIn: ApiState;
        signOut: ApiState;
        setAccountPassword: ApiState;
        confirmEmailLink: ApiState;
        requestPasswordReset: ApiState;
        updatePassword: ApiState;
        changePassword: ApiState;
        changeEmail: ApiState;
        updateUserProfile: ApiState;
        updateVideoEditorPreference: ApiState;
        fetchUserProfile: ApiState;
    };
}

const initialState: AuthState = {
  user: null,
  session: null,
  appUser: null,
  loading: true,
  initialized: false,
  error: null,
  passwordSetConfirmed: false,
  api: {
    initialize: createInitialApiState(),
    signIn: createInitialApiState(),
    signOut: createInitialApiState(),
    setAccountPassword: createInitialApiState(),
    confirmEmailLink: createInitialApiState(),
    requestPasswordReset: createInitialApiState(),
    updatePassword: createInitialApiState(),
    changePassword: createInitialApiState(),
    changeEmail: createInitialApiState(),
    updateUserProfile: createInitialApiState(),
    updateVideoEditorPreference: createInitialApiState(),
    fetchUserProfile: createInitialApiState(),
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
    },
    setSession: (state, action: PayloadAction<Session | null>) => {
      state.session = action.payload;
    },
    setAppUser: (state, action: PayloadAction<AppUser | null>) => {
      state.appUser = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.session = null;
      state.appUser = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
        // Initialize Auth
    builder.addCase(initializeAuth.pending, (state) => {
      state.loading = true;
      state.error = null;
      setApiPending(state.api.initialize);
    });
    builder.addCase(initializeAuth.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.session = action.payload.session;
      state.appUser = action.payload.appUser;
      state.loading = false;
      state.initialized = true;
      state.error = null;
      setApiSuccess(state.api.initialize);
    });
    builder.addCase(initializeAuth.rejected, (state, action) => {
      state.loading = false;
      state.initialized = true;
      state.error = action.payload as string;
      setApiRejected(state.api.initialize, action.payload as string);
    });

        // Sign In
    builder.addCase(signIn.pending, (state) => {
      state.loading = true;
      state.error = null;
      setApiPending(state.api.signIn);
    });
    builder.addCase(signIn.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.session = action.payload.session;
      state.appUser = action.payload.appUser;
      state.loading = false;
      state.error = null;
      setApiSuccess(state.api.signIn);
    });
    builder.addCase(signIn.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      setApiRejected(state.api.signIn, action.payload as string);
    });

        // First password for a checkout-provisioned account. `passwordSetConfirmed` is what releases
        // the set-password gate; the profile row and the JWT claim both lag behind this write.
    builder.addCase(setAccountPassword.pending, (state) => {
      setApiPending(state.api.setAccountPassword);
    });
    builder.addCase(setAccountPassword.fulfilled, (state) => {
      state.passwordSetConfirmed = true;
      if (state.appUser) {
        state.appUser.password_set = true;
      }
      setApiSuccess(state.api.setAccountPassword);
    });
    builder.addCase(setAccountPassword.rejected, (state, action) => {
      setApiRejected(state.api.setAccountPassword, apiErrorMessage(action.payload, 'Could not set your password'));
    });

        // Exchange an emailed link's token_hash for a session (/auth/confirm). Set here rather than
        // left to the onAuthStateChange listener so the store is already updated by the time the
        // container navigates — see the thunk's doc comment for the race that would otherwise cause.
    builder.addCase(confirmEmailLink.pending, (state) => {
      setApiPending(state.api.confirmEmailLink);
    });
    builder.addCase(confirmEmailLink.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.session = action.payload.session;
      setApiSuccess(state.api.confirmEmailLink);
    });
    builder.addCase(confirmEmailLink.rejected, (state, action) => {
      setApiRejected(state.api.confirmEmailLink, action.payload as string);
    });

        // Request a password reset email. Deliberately has no fulfilled payload worth storing —
        // the screen must not react differently depending on whether the account exists.
    builder.addCase(requestPasswordReset.pending, (state) => {
      setApiPending(state.api.requestPasswordReset);
    });
    builder.addCase(requestPasswordReset.fulfilled, (state) => {
      setApiSuccess(state.api.requestPasswordReset);
    });
    builder.addCase(requestPasswordReset.rejected, (state, action) => {
      setApiRejected(state.api.requestPasswordReset, action.payload as string);
    });

        // Set the new password
    builder.addCase(updatePassword.pending, (state) => {
      setApiPending(state.api.updatePassword);
    });
    builder.addCase(updatePassword.fulfilled, (state, action) => {
      if (action.payload.user) {
        state.user = action.payload.user;
      }
      setApiSuccess(state.api.updatePassword);
    });
    builder.addCase(updatePassword.rejected, (state, action) => {
      setApiRejected(state.api.updatePassword, action.payload as string);
    });

        // Change password from the account page (re-authenticates first)
    builder.addCase(changePassword.pending, (state) => {
      setApiPending(state.api.changePassword);
    });
    builder.addCase(changePassword.fulfilled, (state, action) => {
      if (action.payload.user) {
        state.user = action.payload.user;
      }
      setApiSuccess(state.api.changePassword);
    });
    builder.addCase(changePassword.rejected, (state, action) => {
      setApiRejected(state.api.changePassword, action.payload as string);
    });

        // Change email. Note there is no user update here — the address does not move until the
        // confirmation link is clicked, so the session still holds the old one on success.
    builder.addCase(changeEmail.pending, (state) => {
      setApiPending(state.api.changeEmail);
    });
    builder.addCase(changeEmail.fulfilled, (state) => {
      setApiSuccess(state.api.changeEmail);
    });
    builder.addCase(changeEmail.rejected, (state, action) => {
      setApiRejected(state.api.changeEmail, action.payload as string);
    });

        // Update profile
    builder.addCase(updateUserProfile.pending, (state) => {
      setApiPending(state.api.updateUserProfile);
    });
    builder.addCase(updateUserProfile.fulfilled, (state, action) => {
      state.appUser = action.payload;
      setApiSuccess(state.api.updateUserProfile);
    });
    builder.addCase(updateUserProfile.rejected, (state, action) => {
      setApiRejected(state.api.updateUserProfile, action.payload as string);
    });

        // Video editor preference + Resolve acknowledgement (one PATCH, see the thunk).
    builder.addCase(updateVideoEditorPreference.pending, (state) => {
      setApiPending(state.api.updateVideoEditorPreference);
    });
    builder.addCase(updateVideoEditorPreference.fulfilled, (state, action) => {
      state.appUser = action.payload;
      setApiSuccess(state.api.updateVideoEditorPreference);
    });
    builder.addCase(updateVideoEditorPreference.rejected, (state, action) => {
      setApiRejected(
        state.api.updateVideoEditorPreference,
        apiErrorMessage(action.payload, 'Could not save your video editor preference')
      );
    });

        // Update Session
    builder.addCase('auth/updateSession/fulfilled' as any, (state, action: any) => {
      state.user = action.payload.user;
      state.session = action.payload.session;
      state.appUser = action.payload.appUser;
      state.loading = false;
    });

        // Sign Out
    builder.addCase(signOut.pending, (state) => {
      state.loading = true;
      state.error = null;
      setApiPending(state.api.signOut);
    });
    builder.addCase(signOut.fulfilled, (state) => {
      state.user = null;
      state.session = null;
      state.appUser = null;
      state.loading = false;
      state.error = null;
      state.passwordSetConfirmed = false;
      setApiSuccess(state.api.signOut);
    });
    builder.addCase(signOut.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      setApiRejected(state.api.signOut, action.payload as string);
    });

        // Fetch User Profile
    builder.addCase(fetchUserProfile.pending, (state) => {
      setApiPending(state.api.fetchUserProfile);
    });
    builder.addCase(fetchUserProfile.fulfilled, (state, action) => {
      state.appUser = action.payload;
      state.error = null;
      setApiSuccess(state.api.fetchUserProfile);
    });
    builder.addCase(fetchUserProfile.rejected, (state, action) => {
      state.error = action.payload as string;
      setApiRejected(state.api.fetchUserProfile, action.payload as string);
    });

        // Set Welcome Page Preference
    builder.addCase(setWelcomePagePreference.fulfilled, (state, action) => {
      if (action.payload && state.appUser) {
        state.appUser.welcome_page = action.payload.welcome_page;
      }
    });
    builder.addCase(setWelcomePagePreference.rejected, (_state, action) => {
      console.error('Failed to set welcome page preference:', action.payload);
    });
  },
});

export const {setUser, setSession, setAppUser, setLoading, setInitialized, logout} = authSlice.actions;
export default authSlice.reducer;
